import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { formatDate } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  KeyRound, 
  Shield, 
  Check, 
  Lock, 
  Loader2,
  User,
  Mail,
  Building2,
  Trash2,
  AlertTriangle,
  Bell,
  BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import { registerPushNotifications, isPushPluginAvailable, getPushDiagnostics, getLastRegisterResult } from '../../services/PushService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const MobileSettings = () => {
  const navigate = useNavigate();
  const { 
    operator, 
    deviceToken, 
    trustedUserInfo, 
    setupPin, 
    clearDeviceToken,
    logout,
    tenantSettings,
    api
  } = useMobileAuth();
  
  // PIN setup state
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testingPush, setTestingPush] = useState(false);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/api/v2/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Delete PIN confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Push notification state — native push registers automatically in the native app
  const pushEnabled = window.Capacitor?.isNativePlatform?.() || false;
  const pushAvailable = window.Capacitor?.isNativePlatform?.() || false;
  // Whether THIS installed native build actually bundles the push plugin.
  const pushPluginPresent = isPushPluginAvailable();
  const [pushDevices, setPushDevices] = useState(null); // null = unknown/checking
  const [registering, setRegistering] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [lastPushResult, setLastPushResult] = useState(null);

  const refreshPushStatus = useCallback(async () => {
    if (!pushAvailable || !api) return;
    try {
      const res = await api.get('/api/mobile/push/status');
      setPushDevices(res.data?.registered_devices ?? 0);
    } catch {
      setPushDevices(0);
    }
  }, [pushAvailable, api]);

  useEffect(() => { refreshPushStatus(); }, [refreshPushStatus]);

  const handleRegisterDevice = async () => {
    if (!pushPluginPresent) {
      toast.error('This app version doesn’t include push. Please update from the App Store.');
      return;
    }
    setRegistering(true);
    try {
      await registerPushNotifications(api);
      // Token arrives async via the OS listener; poll status briefly.
      await new Promise((r) => setTimeout(r, 3000));
      await refreshPushStatus();
      // Check actual registration result from PushService
      const result = getLastRegisterResult();
      if (result?.ok === false) {
        toast.error(`Registration failed: ${result.error}`, { duration: 8000 });
      } else if (result?.ok) {
        toast.success('Device registered for push notifications.');
      } else {
        toast.info('Registration requested. If you approved notifications, your device should now be registered.');
      }
    } catch (e) {
      toast.error('Could not register this device for push.');
    } finally {
      setRegistering(false);
    }
  };

  // Send a test push to this device (self-verification)
  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const res = await api.post('/api/mobile/push/test');
      setLastPushResult(res.data?.push_result || null);
      const sent = res.data?.push_result?.sent ?? 0;
      const errors = res.data?.push_result?.errors || [];
      if (sent > 0) {
        toast.success('Test push sent! Check your notification tray.');
      } else {
        const reason = errors[0] || 'unknown reason';
        toast.error(`Delivery failed: ${reason}`, { duration: 10000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send test push.');
    } finally {
      setTestingPush(false);
    }
  };

  // Handle PIN input
  const handlePinInput = useCallback((digit) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
      setError('');
    }
  }, [pin]);

  const handlePinBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handleClearPin = useCallback(() => {
    setPin('');
    setError('');
  }, []);

  // PIN setup handler
  const handlePinSetup = async () => {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await setupPin(pin);
    
    if (result.success) {
      toast.success('PIN set up successfully!');
      setShowPinSetup(false);
      setPin('');
    } else {
      setError(result.error);
      setPin('');
    }
    
    setLoading(false);
  };

  // Delete PIN handler
  const handleDeletePin = () => {
    clearDeviceToken();
    setShowDeleteConfirm(false);
    toast.success('PIN removed. You will need to use email & password next time.');
  };

  // PIN Pad component
  const PinPad = () => (
    <div className="space-y-4">
      {/* PIN Display */}
      <div className="space-y-1.5">
        <Label className="text-[var(--m-text-secondary)] text-xs uppercase flex items-center gap-1.5">
          <Lock className="h-3 w-3" />
          {deviceToken ? 'Enter New PIN' : 'Create PIN'}
        </Label>
        <div className="flex justify-center gap-2 py-3 bg-[var(--m-bg)] rounded-md border border-[var(--m-border-light)]">
          {[0,1,2,3,4,5].map((i) => (
            <div
              key={`opt-${i}`}
              className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? 'bg-[#ffd108]' : 'bg-[var(--m-border-light)]'}`}
              data-testid={`settings-pin-dot-${i}`}
            />
          ))}
        </div>
      </div>
      
      {error && (
        <p className="text-red-400 text-sm text-center bg-red-400/10 py-2 rounded-md" data-testid="pin-setup-error">
          {error}
        </p>
      )}
      
      {/* PIN Pad Grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {[1,2,3,4,5,6,7,8,9].map((digit) => (
          <Button
            key={digit}
            variant="outline"
            className="h-12 text-lg font-bold bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
            onClick={() => handlePinInput(String(digit))}
            disabled={loading}
            data-testid={`settings-pin-digit-${digit}`}
          >
            {digit}
          </Button>
        ))}
        <Button
          variant="outline"
          className="h-12 text-xs bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
          onClick={handleClearPin}
          disabled={loading}
          data-testid="settings-pin-clear"
        >
          Clear
        </Button>
        <Button
          variant="outline"
          className="h-12 text-lg font-bold bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
          onClick={() => handlePinInput('0')}
          disabled={loading}
          data-testid="settings-pin-digit-0"
        >
          0
        </Button>
        <Button
          variant="outline"
          className="h-12 text-lg bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
          onClick={handlePinBackspace}
          disabled={loading}
          data-testid="settings-pin-backspace"
        >
          ←
        </Button>
      </div>
      
      <div className="flex gap-2 mt-2">
        <Button 
          variant="outline"
          onClick={() => {
            setShowPinSetup(false);
            setPin('');
            setError('');
          }}
          className="flex-1 bg-transparent border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
          disabled={loading}
          data-testid="cancel-pin-setup"
        >
          Cancel
        </Button>
        <Button 
          onClick={handlePinSetup} 
          className="flex-1 btn-volt"
          disabled={loading || pin.length < 4}
          data-testid="confirm-pin-setup"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (deviceToken ? 'Update PIN' : 'Set PIN')}
        </Button>
      </div>
    </div>
  );

  return (
    <MobileLayout>
    <div className="min-h-full bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')} 
            className="text-[var(--m-text-secondary)] hover:text-[var(--m-text)]"
            data-testid="settings-back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-[var(--m-text)] font-bold text-lg">Settings</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Profile Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--m-text)] flex items-center gap-2 text-base">
              <User className="h-4 w-4 text-[#ffd108]" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-[var(--m-bg)] rounded-lg">
              <div className="w-12 h-12 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                <User className="h-6 w-6 text-[#ffd108]" />
              </div>
              <div className="flex-1">
                <p className="text-[var(--m-text)] font-medium">{operator?.name || 'Operator'}</p>
                <p className="text-[var(--m-text-secondary)] text-sm flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {operator?.email || 'No email'}
                </p>
                {operator?.client_id && (
                  <p className="text-[var(--m-text-muted)] text-xs flex items-center gap-1 mt-1">
                    <Building2 className="h-3 w-3" />
                    {operator.client_id}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--m-text)] flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-[#ffd108]" />
              Security
            </CardTitle>
            <CardDescription className="text-[var(--m-text-secondary)] text-sm">
              Manage your login security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* PIN Status and Actions */}
            {showPinSetup ? (
              <PinPad />
            ) : (
              <>
                {deviceToken ? (
                  // PIN is set
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-green-400 font-medium">PIN Enabled</p>
                        <p className="text-[var(--m-text-secondary)] text-sm">Quick login is active on this device</p>
                        {trustedUserInfo?.trustedAt && (
                          <p className="text-[var(--m-text-muted)] text-xs mt-1">
                            Set up: {formatDate(trustedUserInfo.trustedAt, tenantSettings?.date_format)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => setShowPinSetup(true)}
                        className="bg-transparent border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
                        data-testid="change-pin-button"
                      >
                        <KeyRound className="h-4 w-4 mr-2" />
                        Change PIN
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="bg-transparent border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                        data-testid="remove-pin-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove PIN
                      </Button>
                    </div>
                  </div>
                ) : (
                  // No PIN set
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-[#ffd108]/10 rounded-lg border border-[#ffd108]/30">
                      <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                        <KeyRound className="h-5 w-5 text-[#ffd108]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[#ffd108] font-medium">PIN Not Set</p>
                        <p className="text-[var(--m-text-secondary)] text-sm">Set up a PIN to sign in faster</p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => setShowPinSetup(true)}
                      className="w-full btn-volt"
                      data-testid="setup-pin-button"
                    >
                      <KeyRound className="h-4 w-4 mr-2" />
                      Set Up PIN
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Notifications Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--m-text)] flex items-center gap-2 text-base">
              <Bell className="h-4 w-4 text-[#ffd108]" />
              Notifications
            </CardTitle>
            <CardDescription className="text-[var(--m-text-secondary)] text-sm">
              Manage push notification preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!pushAvailable ? (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <BellOff className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-muted-foreground font-medium">Push Not Available</p>
                  <p className="text-muted-foreground text-sm">Push notifications are only available on the production domain. Add this app to your home screen for the best experience.</p>
                </div>
              </div>
            ) : pushEnabled ? (
              <div className="space-y-3">
                {!pushPluginPresent ? (
                  <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <BellOff className="h-5 w-5 text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-amber-400 font-medium">Update required</p>
                      <p className="text-[var(--m-text-secondary)] text-sm">This app version doesn’t include push notifications yet. Please update to the latest version from the App Store / Play Store.</p>
                    </div>
                  </div>
                ) : pushDevices > 0 ? (
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Bell className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-green-400 font-medium">This device is registered</p>
                      <p className="text-[var(--m-text-secondary)] text-sm">You'll receive alerts for new jobs and updates.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-[var(--m-bg-card)] rounded-lg border border-[var(--m-border)]">
                    <div className="w-10 h-10 rounded-full bg-[var(--m-bg-elevated)] flex items-center justify-center">
                      <BellOff className="h-5 w-5 text-[var(--m-text-secondary)]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[var(--m-text-body)] font-medium">Not registered on this device</p>
                      <p className="text-[var(--m-text-muted)] text-sm">Tap below and allow notifications when prompted.</p>
                    </div>
                  </div>
                )}

                {pushPluginPresent && pushDevices === 0 && (
                  <Button
                    onClick={handleRegisterDevice}
                    disabled={registering}
                    className="w-full btn-volt"
                    data-testid="settings-register-push-button"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    {registering ? 'Registering…' : 'Register this device'}
                  </Button>
                )}

                <Button
                  onClick={handleTestPush}
                  disabled={testingPush || !pushPluginPresent}
                  className="w-full bg-transparent border border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
                  variant="outline"
                  data-testid="settings-test-push-button"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  {testingPush ? 'Sending…' : 'Send test notification'}
                </Button>

                <button
                  onClick={() => setShowDiag((s) => !s)}
                  className="w-full text-center text-[11px] text-[var(--m-text-muted)] underline pt-1"
                  data-testid="settings-push-diag-toggle"
                >
                  {showDiag ? 'Hide' : 'Show'} push diagnostics
                </button>
                {showDiag && (
                  <pre
                    className="text-[10px] leading-relaxed text-[var(--m-text-secondary)] bg-black/40 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all"
                    data-testid="settings-push-diagnostics"
                  >
{JSON.stringify({ ...getPushDiagnostics(), registeredDevices: pushDevices, lastPushResult }, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-[var(--m-bg-card)] rounded-lg border border-[var(--m-border)]">
                <div className="w-10 h-10 rounded-full bg-[var(--m-bg-elevated)] flex items-center justify-center">
                  <BellOff className="h-5 w-5 text-[var(--m-text-secondary)]" />
                </div>
                <div className="flex-1">
                  <p className="text-[var(--m-text-body)] font-medium">Push Notifications</p>
                  <p className="text-[var(--m-text-muted)] text-sm">Install the EQUIPR CORE app from the App Store to receive push notifications</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Password Section */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--m-text)] flex items-center gap-2 text-base">
              <Lock className="h-4 w-4 text-[#ffd108]" />
              Password
            </CardTitle>
            <CardDescription className="text-[var(--m-text-secondary)] text-sm">
              Change your login password
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showChangePassword ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[var(--m-text-secondary)]">Current Password</Label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)]"
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[var(--m-text-secondary)]">New Password</Label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)]"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[var(--m-text-secondary)]">Confirm Password</Label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-lg px-3 py-2.5 text-sm text-[var(--m-text)]"
                    data-testid="confirm-password-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowChangePassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}
                    className="flex-1 bg-transparent border-[var(--m-border-light)] text-[var(--m-text-secondary)]"
                    data-testid="cancel-change-password"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                    className="flex-1 btn-volt"
                    data-testid="confirm-change-password"
                  >
                    {changingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowChangePassword(true)}
                className="w-full bg-transparent border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
                data-testid="change-password-button"
              >
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Logout Button */}
        <Button 
          variant="outline"
          onClick={() => {
            logout();
            navigate('/mobile/login');
          }}
          className="w-full bg-transparent border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/50"
          data-testid="settings-logout-button"
        >
          Sign Out
        </Button>
      </div>

      {/* Delete PIN Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              Remove PIN?
            </DialogTitle>
            <DialogDescription className="text-[var(--m-text-secondary)]">
              This will remove your PIN from this device. You'll need to sign in with your email and password next time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 bg-transparent border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:bg-[var(--m-bg-elevated)] hover:text-[var(--m-text)]"
              data-testid="cancel-delete-pin"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePin}
              className="flex-1 bg-red-500 hover:bg-red-600 text-[var(--m-text)]"
              data-testid="confirm-delete-pin"
            >
              Remove PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </MobileLayout>
  );
};

export default MobileSettings;
